import axios from "../../axiosInstance";
import requests from '../../requests';
import { nanoid } from 'nanoid';

import { takeLatest, takeEvery, all, put, call, delay, select, debounce } from "redux-saga/effects";

import { diagramActionTypes } from "./diagram.types";
// import { mainActionTypes } from "../main/main.types";
import {
	// fetchDiagram,
	fetchDiagramSuccess,
	fetchDiagramFailure,
} from "./diagram.actions";
import {
	// fetchDiagramMain,
	fetchDiagramSuccessMain,
	fetchDiagramFailureMain,
} from "../main/main.actions";

import { showNotify } from "../notify/notify.actions";

import {
    getOrCreateDiagramDoc,
    getUndoManager,
    getDiagramDoc,
    whenDiagramDocSynced,
    isDiagramDocEmpty,
} from '../../lib/yjs';
import { hydrateDoc } from '../../lib/yjs/hydrate';
import { yElements } from '../../lib/yjs/schema';
import { deleteElements } from '../../lib/yjs/mutators/elements';
import { addAreaForFocusedElements } from '../../lib/yjs/mutators/areas';
import { setTableDisplayHeight } from '../../lib/yjs/mutators/columns';

import { computeFitViewport } from '../../utils/viewport/fit-viewport';
import { getVisibleColumnsCount, heightForRows } from '../../utils/canvas/tableClipping';

import {
	updateFocusedInsideElement,
	updateFocusedIds,
	enableCanvasPricingPlanLimit,
	disableBtnNewArea,
	applyScaleToFit,
	applyUserViewport,
	updateScaleToFit,
} from './diagram.actions';
import { selectFocusedIds } from './diagram.selectors';

// ---------------------------------------------------------------------------
// Pricing-plan helper (mirrors the checkAreas logic in the reducer)
// ---------------------------------------------------------------------------
const AREAS_LIMIT_FREE = 10;
const AREAS_LIMIT_MAX  = 200;

function checkAreasLimit(currentCount, addCount, isUp) {
	const limit = isUp ? AREAS_LIMIT_MAX : AREAS_LIMIT_FREE;
	return (currentCount + addCount) <= limit;
}

// ---------------------------------------------------------------------------
// Sagas
// ---------------------------------------------------------------------------

export function* fetchDiagramRequest({ payload: { diagramId } }) {
	try {
		const url = `${requests.diagrams}/${diagramId}`;
		const { data, error } = yield axios.get(url, {});
		if (error) {
			yield put(fetchDiagramFailure(error));
			yield put(fetchDiagramFailureMain(error));
			yield put(showNotify({ message: 'something_wrong' }));
		} else {
			yield put(fetchDiagramSuccess(data));
			yield put(fetchDiagramSuccessMain(data));
		}
	} catch (e) {
		// A real HTTP error means the diagram can't be loaded — wrong URL,
		// not found, or no access. Flag it so the canvas swaps to the
		// not-found view (ModalDiagramNotFound) instead of the editor.
		if (e?.response) {
			yield put(fetchDiagramFailure(e));
			yield put(fetchDiagramFailureMain(e));
			return;
		}

		// No HTTP response (offline / API down): keep the dev fallback so the
		// canvas still has something to render locally.
		yield delay(1000);
		const testData = {
			id: 'default',
			title: 'Delivery App (Uber Eats like)',
			description: 'Project X is a fictional experimental database focused on tracking anomalies and scientific experiments. Ideal for showcasing advanced data modeling concepts like enums, JSON fields, and complex relationships.',
			db_engine: 'postgresql',
			// data: {}
		};
		yield put(fetchDiagramSuccess(testData));
		yield put(fetchDiagramSuccessMain(testData));
	}
}

export function* onFetchDiagram() {
    yield takeLatest(diagramActionTypes.FETCH_DIAGRAM_REQUEST, fetchDiagramRequest);
}

function* hydrateOnDiagramSuccess({ payload }) {
    if (!payload?.data?.id) return;
    const doc = getOrCreateDiagramDoc(payload.data.id);
    // Wait for IndexedDB to finish restoring any persisted state for this
    // diagram. If state was restored, the doc is non-empty and we skip
    // hydration — re-hydrating from the API payload would clobber the
    // user's local edits made between sessions.
    yield call(whenDiagramDocSynced);
    if (!isDiagramDocEmpty(doc)) return;
    hydrateDoc(doc, payload.data);
}

function* onHistoryUndo() {
    getUndoManager()?.undo();
}

function* onHistoryRedo() {
    getUndoManager()?.redo();
}

export function* onFetchDiagramSuccess() {
    yield takeEvery(diagramActionTypes.FETCH_DIAGRAM_SUCCESS, hydrateOnDiagramSuccess);
}

export function* onHistoryUndoWatcher() {
    yield takeEvery(diagramActionTypes.HISTORY_UNDO, onHistoryUndo);
}

export function* onHistoryRedoWatcher() {
    yield takeEvery(diagramActionTypes.HISTORY_REDO, onHistoryRedo);
}

// 2a — FOCUS_ON_COLUMN_BELOW
function* onFocusOnColumnBelow({ payload }) {
    const doc = getDiagramDoc();
    if (!doc) return;
    const yTable = yElements(doc).get(payload.tableId);
    if (!yTable) return;
    const order = yTable.get('columnsOrder')?.toArray() ?? [];
    const idx = order.indexOf(payload.columnId);
    if (idx < 0 || idx >= order.length - 1) return;
    const nextColumnId = order[idx + 1];
    yield put(updateFocusedInsideElement(payload.tableId, nextColumnId, idx + 1, 'column'));
}

// 2b — FOCUS_ON_COLUMN_ABOVE
function* onFocusOnColumnAbove({ payload }) {
    const doc = getDiagramDoc();
    if (!doc) return;
    const yTable = yElements(doc).get(payload.tableId);
    if (!yTable) return;
    const order = yTable.get('columnsOrder')?.toArray() ?? [];
    const idx = order.indexOf(payload.columnId);
    if (idx < 0) return;
    if (idx > 0) {
        const prevColumnId = order[idx - 1];
        yield put(updateFocusedInsideElement(payload.tableId, prevColumnId, idx - 1, 'column'));
    } else {
        // idx === 0 — focus the table header
        yield put(updateFocusedInsideElement(payload.tableId, null, null, 'header'));
    }
}

// 2c — FOCUS_ON_FIRST_COLUMN
function* onFocusOnFirstColumn({ payload }) {
    const doc = getDiagramDoc();
    if (!doc) return;
    const yTable = yElements(doc).get(payload.tableId);
    if (!yTable) return;
    const order = yTable.get('columnsOrder')?.toArray() ?? [];
    if (!order.length) return;
    yield put(updateFocusedInsideElement(payload.tableId, order[0], 0, 'column'));
}

// 2d — FOCUS_ALL_ELEMENTS
function* onFocusAllElements() {
    const doc = getDiagramDoc();
    if (!doc) return;
    const entries = [...yElements(doc).entries()];
    const allIds = entries.map(([id]) => id);
    yield put(updateFocusedIds(allIds));
}

// 2e — TOGGLE_BTN_NEW_AREA (saga handles the data-mutation path only)
function* onToggleBtnNewArea({ payload }) {
    if (!payload.isKeyClick) return; // the else branch (UI toggle) is handled by the reducer

    const allSelected = yield select(selectFocusedIds);
    const doc = getDiagramDoc();
    if (!doc) return;
    const focusedElementsIds = allSelected.filter(id => {
        const el = yElements(doc).get(id);
        return el && el.get('type') !== 'area';
    });
    if (!focusedElementsIds.length) return;

    const isUp = yield select((state) => state.diagram.is_u_p ?? false);
    const areaCount = [...yElements(doc).values()].filter(el => el.get('type') === 'area').length;
    if (!checkAreasLimit(areaCount, 1, isUp)) {
        yield put(enableCanvasPricingPlanLimit('areas'));
        return;
    }

    const areaId = nanoid();

    addAreaForFocusedElements(doc, {
        focusedElementsIds,
        areaProps: { id: areaId },
    });

    yield put(updateFocusedIds([areaId]));
    yield put(disableBtnNewArea());
}

// UPDATE_SCALE_TO_FIT — reads Yjs, computes zoom-to-fit, dispatches APPLY_SCALE_TO_FIT
// const INITIAL_SCALE_VALUE_SAGA = 0.9;
const INITIAL_SCALE_VALUE_SAGA = 0.95;

function* onUpdateScaleToFit({ payload: _payload }) {
    const doc = getDiagramDoc();
    const state = yield select((s) => s.diagram);

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let hasContent = false;

    if (doc) {
        yElements(doc).forEach((el) => {
            const x = el.get('x') ?? 0, y = el.get('y') ?? 0;
            const w = el.get('width') ?? 0, h = el.get('height') ?? 0;
            if (x < minX) minX = x;
            if (y < minY) minY = y;
            if (x + w > maxX) maxX = x + w;
            if (y + h > maxY) maxY = y + h;
            hasContent = true;
        });
    }

    if (hasContent && isFinite(minX) && isFinite(maxX)) {
        const { scale, x, y } = computeFitViewport(
            { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
            {
                windowW: typeof window !== 'undefined' ? window.innerWidth : 1440,
                windowH: typeof window !== 'undefined' ? window.innerHeight : 900,
                maxZoom: 1.08,
            }
        );
        yield put(applyScaleToFit(scale, x, y));
    } else {
        // No content — reset to initial scale
        const newScale = INITIAL_SCALE_VALUE_SAGA;
        const centerX  = typeof window !== 'undefined' ? window.innerWidth  / 2 : 720;
        const centerY  = typeof window !== 'undefined' ? window.innerHeight / 2 : 450;
        const zoomFactor = newScale / (state.scale || INITIAL_SCALE_VALUE_SAGA);
        const newOriginX = (state.originXY.x - centerX) * zoomFactor + centerX;
        const newOriginY = (state.originXY.y - centerY) * zoomFactor + centerY;
        yield put(applyScaleToFit(newScale, newOriginX, newOriginY));
    }
}

// 2f — CLEAR_NEW_PEN
function* onClearNewPen() {
    const doc = getDiagramDoc();
    if (!doc) return;
    const newPen = yield select((state) => state.diagram.newPen);
    if (!newPen?.id) return;
    const idsToDelete = [];
    yElements(doc).forEach((el, id) => {
        if (el.get('newPenId') === newPen.id) idsToDelete.push(id);
    });
    if (idsToDelete.length) {
        deleteElements(doc, idsToDelete);
    }
}

// 2g — UPDATE_FOCUSED_INSIDE_ELEMENT (auto-reveal clipped column)
// Focusing a column hidden by table clipping reveals it — grows the clip to
// include that row (or clears it when that reaches natural height). Runs on
// every UPDATE_FOCUSED_INSIDE_ELEMENT so no caller needs to know about
// clipping (keyboard nav, Enter-add, search commit, context menu).
// eslint-disable-next-line require-yield -- purely synchronous Yjs mutation, no effects to yield
export function* onAutoRevealFocusedColumn({ payload }) {
    if (payload?.insideElementType !== 'column' || !payload.tableId) return;
    const doc = getDiagramDoc();
    if (!doc) return;
    const yTable = yElements(doc).get(payload.tableId);
    if (!yTable || yTable.get('type') !== 'table') return;
    const displayHeight = yTable.get('displayHeight');
    if (displayHeight == null) return;

    const order = yTable.get('columnsOrder')?.toArray() ?? [];
    const index = payload.columnId ? order.indexOf(payload.columnId) : (payload.columnIndex ?? -1);
    if (index < 0) return;

    const el = { type: 'table', height: yTable.get('height'), displayHeight };
    const visible = getVisibleColumnsCount(el, order.length);
    if (index < visible) return;

    const targetRows = index + 1;
    const next = targetRows >= order.length ? null : heightForRows(targetRows);
    setTableDisplayHeight(doc, payload.tableId, next);
}

// ---------------------------------------------------------------------------
// Viewport persistence helpers
// ---------------------------------------------------------------------------
const VIEWPORT_LS_PREFIX = 'duv:';
const VIEWPORT_LS_DEBOUNCE_MS = 150;
const VIEWPORT_API_DEBOUNCE_MS = 1000;

export const VIEWPORT_TRIGGER_ACTIONS = [
    diagramActionTypes.UPDATE_SCALE,        // mouse-scroll zoom
    diagramActionTypes.UPDATE_ORIGIN_XY,    // pan
    // Button / keyboard-shortcut zoom paths. These mutate scale + originXY in the
    // reducer just like scroll-zoom, but dispatch their own action types — so
    // without listing them here, zoom-to-fit / zoom-to-100% / zoom +/- changed the
    // view without ever persisting it.
    diagramActionTypes.APPLY_SCALE_TO_FIT,  // zoom to fit
    diagramActionTypes.APPLY_VIEWPORT,      // search tween frames (debounced persistence absorbs the burst)
    diagramActionTypes.RESET_SCALE,         // zoom to 100%
    diagramActionTypes.UPDATE_SCALE_PLUS,   // zoom + button
    diagramActionTypes.UPDATE_SCALE_MINUS,  // zoom - button
    diagramActionTypes.TOGGLE_BTN_MINIMAP,
    // SET_ELEMENTS_VIEW_MODE deliberately absent: view mode is session-only
    // (see buildViewport below), so changing it persists nothing.
];

function lsKey(diagramId) {
    return `${VIEWPORT_LS_PREFIX}${diagramId}`;
}

function readLocalViewport(diagramId) {
    try {
        const raw = localStorage.getItem(lsKey(diagramId));
        return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
}

function writeLocalViewport(diagramId, vp) {
    try {
        localStorage.setItem(lsKey(diagramId), JSON.stringify({ ...vp, saved_at: Date.now() }));
    } catch (_) {}
}

export function* applyViewportFromFetchSuccess({ payload }) {
    const diagramId = payload?.data?.id;
    if (!diagramId) return;

    const local = readLocalViewport(diagramId);
    const server = payload?.data?.user_viewport ?? null;

    let vp;
    let useServer = false;
    if (local && server) {
        const localTs  = local.saved_at ?? 0;
        const serverTs = server.updated_at ? new Date(server.updated_at).getTime() : 0;
        useServer = serverTs > localTs;
        vp = useServer ? server : local;
    } else {
        useServer = !local && !!server;
        vp = local ?? server;
    }

    if (!vp) {
        // No saved viewport — first open or freshly invited user. Wait for the
        // doc to be synced+hydrated and scale to fit all content. (Grid is a
        // preference, rendered straight from preferences.editor.grid.)
        yield call(whenDiagramDocSynced);
        yield delay(0); // let the concurrent hydrateDoc call finish synchronously
        yield put(updateScaleToFit());
        return;
    }

    if (useServer) writeLocalViewport(diagramId, vp);
    // Stale stored keys (grid_style / scroll_behavior) pass through here but are
    // ignored by the APPLY_USER_VIEWPORT reducer.
    yield put(applyUserViewport(vp));
}

// Only true per-diagram view state is persisted. grid_style / scroll_behavior /
// diagram_theme were removed — grid moved to user preferences (diagram.grid);
// the other two are dead.
function* buildViewport() {
    const state = yield select((s) => s.diagram);
    return {
        scale:        state.scale,
        origin_x:     state.originXY?.x ?? 0,
        origin_y:     state.originXY?.y ?? 0,
        // view_mode is deliberately NOT persisted (user decision): reopening a
        // diagram in keys-only mode read as "where are my columns?" — the
        // toggle works for the session, but every load starts at 'full' (the
        // reducer default). Restore the line below to persist it again.
        // view_mode:    state.viewMode,
        minimap_open: state.isBtnMinimapEnabled,
    };
}

function* saveViewportToLocal() {
    const diagramId = yield select((s) => s.main?.diagramDetailsCanvas?.id);
    if (!diagramId) return;
    const vp = yield call(buildViewport);
    writeLocalViewport(diagramId, vp);
}

function* saveViewportToBackend() {
    const diagramId = yield select((s) => s.main?.diagramDetailsCanvas?.id);
    if (!diagramId) return;
    const vp = yield call(buildViewport);
    try {
        yield call([axios, axios.patch], `${requests.diagrams}/${diagramId}/viewport`, vp);
    } catch (_) {}
}

export function* diagramSagas() {
    yield all([
        call(onFetchDiagram),
        call(onFetchDiagramSuccess),
        call(onHistoryUndoWatcher),
        call(onHistoryRedoWatcher),
        takeEvery(diagramActionTypes.FOCUS_ON_COLUMN_BELOW,  onFocusOnColumnBelow),
        takeEvery(diagramActionTypes.FOCUS_ON_COLUMN_ABOVE,  onFocusOnColumnAbove),
        takeEvery(diagramActionTypes.FOCUS_ON_FIRST_COLUMN,  onFocusOnFirstColumn),
        takeEvery(diagramActionTypes.FOCUS_ALL_ELEMENTS,     onFocusAllElements),
        takeEvery(diagramActionTypes.TOGGLE_BTN_NEW_AREA,    onToggleBtnNewArea),
        takeEvery(diagramActionTypes.CLEAR_NEW_PEN,          onClearNewPen),
        takeEvery(diagramActionTypes.UPDATE_SCALE_TO_FIT,    onUpdateScaleToFit),
        takeEvery(diagramActionTypes.UPDATE_FOCUSED_INSIDE_ELEMENT, onAutoRevealFocusedColumn),
        takeEvery(diagramActionTypes.FETCH_DIAGRAM_SUCCESS,  applyViewportFromFetchSuccess),
        debounce(VIEWPORT_LS_DEBOUNCE_MS,  VIEWPORT_TRIGGER_ACTIONS, saveViewportToLocal),
        debounce(VIEWPORT_API_DEBOUNCE_MS, VIEWPORT_TRIGGER_ACTIONS, saveViewportToBackend),
    ]);
}
